import React, { useEffect, useState } from "react";
import { Row, Col, Table, Typography, Divider } from "antd";
import { useSelector } from "react-redux";

import {
  numberFormat,
  convertNumberToWords,
  formatToCompanyTimezone,
  numberFormatTax,
} from "../../../lib/functions";
import { salesRecordWithDetailsApi } from "../../../apiservice/sales";
import { getSundryDebitorBalanceApi } from "../../../apiservice/accounts";

import "./SalesVoucherBodyV2.css";
import { selectAuthInfo } from "../../Auth/authSelectors";

const { Title, Text } = Typography;

const SalesVoucherBody = ({ sale_id, printMode = false, hideBonusItems = false, showStamp, showSignature, showShipping, setCustomerEmail }) => {
  const [data, setData] = useState(null);
  const [accDue, setAccDue] = useState(null);

  const { institutionData } = useSelector((state) => state.lookups);

  const authInfo = useSelector(selectAuthInfo);

  const currency = authInfo?.currency;
  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const isIndiaGst = authInfo?.gst_enabled === "yes";
  const showHSN = isIndia && isIndiaGst;
  const taxLabel = isIndia ? "GST" : "VAT";
  const isGSTDisabled =
    isIndia && authInfo?.gst_enabled == "no";

  const isBonusItem = (item) => Number(item.item_rate) === 0;


  useEffect(() => {
    if (!sale_id) return;
    salesRecordWithDetailsApi({ sale_id, from: "voucher" }).then((res) => {
      if (res?.data?.length) setData(res.data[0]);

      if (res?.data?.[0]?.email_address) {
        setCustomerEmail?.(res.data[0].email_address);
      }

    });
  }, [sale_id]);

  useEffect(() => {
    if (!data) return;
    getSundryDebitorBalanceApi({ customerId: data.acc_id }).then((res) => {
      if (res?.data?.accounts?.length)
        setAccDue(res.data.accounts[0].balance);
    });
  }, [data]);


  if (!data) return null;

  // Merging similar product rows
  function mergeVoucherItems(items) {
    const map = {};

    items.forEach(item => {
      const key = [
        item.item_id,
        item.item_description || "",
        item.batch_id || 0,
        item.packing_name || "",
        item.hsn_sac_code || "",
        Number(item.item_rate || 0).toFixed(2),
        Number(item.item_discount_per || 0),
        Number(item.item_tax_per || 0),
        Number(item.item_rate) === 0 ? "BONUS" : "NORMAL"
      ].join("|");

      if (!map[key]) {
        map[key] = {
          ...item,
          item_qty: Number(item.item_qty || 0),
          item_total: Number(item.item_total || 0)
        };
      } else {
        map[key].item_qty += Number(item.item_qty || 0);
        map[key].item_total += Number(item.item_total || 0);
      }
    });

    return Object.values(map);
  }


  const isIndividual = data.is_cal_type === "individual";

  const rawItems = data.details || [];
  const items = mergeVoucherItems(rawItems);
  const visibleItems = hideBonusItems
    ? items.filter((item) => !isBonusItem(item))
    : items;
  const hasBonusItems = items.some(isBonusItem);

  /* ================= CALCULATIONS (UNCHANGED) ================= */
  const totals = items.reduce(
    (acc, d) => {
      const qty = d.item_qty || d.sale_qty || 0;
      const rate = Number(d.item_rate || 0);
      let base = qty * rate;

      const discountAmt =
        (Number(d.item_discount_per || 0) / 100) * base;
      const afterDiscount = base - discountAmt;
      const taxAmt =
        (Number(d.item_tax_per || 0) / 100) * afterDiscount;

      acc.subTotal += base;
      acc.discountTotal += discountAmt;
      acc.taxTotal += taxAmt;
      acc.grandTotal += afterDiscount + taxAmt;

      return acc;
    },
    { subTotal: 0, discountTotal: 0, taxTotal: 0, grandTotal: 0 }
  );

  const totalTax = isIndividual ? totals.taxTotal : data.total_tax;


  const serviceCost = Number(data.other_service_cost || 0);

  const grandTotal = isIndividual
    ? totals.grandTotal + serviceCost
    : (data.total_amount || 0) + serviceCost;
  /* ================= TABLE COLUMNS (UNCHANGED) ================= */
  const columns = isIndividual
    ? [
      {
        title: "#", width: 25, render: (_, __, i) => i + 1, onCell: () => ({
          style: { maxWidth: 28 }
        })
      },
      {
        title: "Item",
        dataIndex: "item_name",
        className: "col-item",
        width: showHSN ? 200 : 260,
        render: (text, record) => (
          <div className="item-cell">
            {text}
            {isBonusItem(record) && (
              <span className="bonus-badge">BONUS</span>
            )}
            {record.batch_name && (
              <div
                style={{
                  fontSize: 11,
                  color: "#888",
                  marginTop: 2,
                }}
              >
                Batch: {record.batch_name}
              </div>
            )}
            {record.item_description && (
              <div
                style={{
                  fontSize: 11,
                  color: "#666",
                  marginTop: 2,
                  whiteSpace: "pre-wrap"
                }}
              >
                {record.item_description}
              </div>
            )}
          </div>
        ),
        onCell: () => ({
          style: { minWidth: 100 }
        })
      },
      ...(showHSN
        ? [
          {
            title: "HSN/SAC",
            dataIndex: "hsn_sac_code",
            align: "center",
            onCell: () => ({
              style: { minWidth: 80 }
            })
          },
        ]
        : []),
      ...((authInfo?.subscription.toLowerCase().includes("pharma") || authInfo?.subscription.toLowerCase().includes("pharmabooks"))
        ? [
          {
            title: "MRP Rate",
            dataIndex: "mrp_rate",
            align: "right",
            render: (v) => numberFormat(v),
            onCell: () => ({
              style: { minWidth: 80 }
            })
          },
        ]
        : []),
      {
        title: "Rate",
        dataIndex: "item_rate",
        align: "right",
        render: (v) => numberFormat(v),
        onCell: () => ({
          style: { minWidth: 50 }
        })
      },
      {
        title: `Qty`,
        // dataIndex: "item_qty",
        align: "right", width: 75,
        render: (_, d) => `${d.item_qty} ${d.unit_symbol}`,
        onCell: () => ({
          style: { minWidth: 30 }
        })
      },
      {
        title: "Disct %",
        dataIndex: "item_discount_per",
        align: "right",
        onCell: () => ({
          style: { minWidth: 40 }
        })
      },
      ...(!isGSTDisabled
        ? [
          {
            title: `${taxLabel} %`,
            dataIndex: "item_tax_per",
            align: "right",
            width: 50,
          },
        ]
        : []),
      {
        title: "Amount",
        align: "right",
        render: (_, d) => {
          let total = d.item_qty * d.item_rate;
          total -= (d.item_discount_per / 100) * total;
          total += (d.item_tax_per / 100) * total;
          return numberFormat(total);
        },
        onCell: () => ({
          style: { minWidth: 90 }
        })
      },
    ]
    : [
      {
        title: "#", width: 25, render: (_, __, i) => i + 1, onCell: () => ({
          style: { maxWidth: 28 }
        })
      },
      {
        title: "Item",
        dataIndex: "item_name",
        className: "col-item",
        render: (text, record) => (
          <div className="item-cell">
            {text}
            {isBonusItem(record) && (
              <span className="bonus-badge">BONUS</span>
            )}
            {record.batch_name && (
              <div
                style={{
                  fontSize: 11,
                  color: "#888",
                  marginTop: 2,
                }}
              >
                Batch: {record.batch_name}
              </div>
            )}
            {record.item_description && (
              <div
                style={{
                  fontSize: 11,
                  color: "#666",
                  marginTop: 2,
                  whiteSpace: "pre-wrap",
                }}
              >
                {record.item_description}
              </div>
            )}
          </div>
        ),
        onCell: () => ({
          style: { minWidth: 100 }
        })
      },
      ...(showHSN
        ? [
          {
            title: "HSN/SAC",
            dataIndex: "hsn_sac_code",
            align: "center",
            width: 100,
          },
        ]
        : []),
      {
        title: "Rate",
        dataIndex: "item_rate",
        align: "right",
        render: (v) => numberFormat(v),
        onCell: () => ({
          style: { minWidth: 50 }
        })
      },
      {
        title: "Qty",
        // dataIndex: "item_qty", 
        align: "right", onCell: () => ({
          style: { minWidth: 50 }
        }),
        render: (_, d) => `${d.item_qty} ${d.unit_symbol}`,
      },
      {
        title: "Amount",
        dataIndex: "item_total",
        align: "right",
        render: (v) => numberFormat(v),
        onCell: () => ({
          style: { minWidth: 90 }
        })
      },
    ];

  return (
    <div className="invoice-v2">
      {/* ================= HEADER ================= */}
      <Row justify="space-between" align="top" className="invoice-header ">
        <Col flex="auto">
          <div className="company-block">

            <div>
              <Title level={4}>{authInfo?.institution_name}</Title>
              <Text className="wrap-text">{authInfo?.address}</Text>
            </div>
          </div>
        </Col>

        <Col flex="320px" className="invoice-title">
          <Title level={2}>INVOICE</Title>
          {/* <Text>Invoice #: {data.sale_voucher_no}</Text><br />
          <Text>
            Date: {formatToCompanyTimezone(data.created_date)?.split(" ")[0]}
          </Text><br />
          <Text>Due Date: {data.due_date || "-"}</Text> */}
        </Col>
      </Row>

      <Divider />

      {/* ================= BILL TO ================= */}
      <Row className="bill-invoice-row" gutter={16} >
        {/* LEFT: Bill To */}
        <Col flex="0 0 320px" className="bill-to">
          <Text strong>Bill To</Text><br />
          <Text >Name : &nbsp;{data.acc_name}</Text><br />
          {/* {data?.institution_name && <><Text>Institution : &nbsp;{data.institution_name}</Text>
            <br /></>} */}
          {data?.acc_mgr && <Text className="wrap-text">
            {authInfo?.country?.trim().toLowerCase() === "united arab emirates" &&
              (authInfo?.subscription.toLowerCase().includes("pharma") || authInfo?.subscription.toLowerCase().includes("pharmabooks"))
              ? "Rep. : "
              : "Account Manager : "}{" "}
            {data.acc_mgr && data.acc_mgr}
          </Text>}
          <Text className="wrap-text">Address : &nbsp; {data.address}</Text>
          <Text>Contact No : &nbsp; +{data.contact_no}</Text><br />
          {data.vat_no && <><Text>
            {authInfo?.country?.trim().toLowerCase() === "united arab emirates"
              ? "TRN No: "
              : "Tax Reg No: "}{" "}
            {data.vat_no}
          </Text><br /></>}
          {data.licence_no && <Text>License No : {data.licence_no}</Text>}
          <br />
          {showShipping && data.shipping_address && (
            <>
              <br />
              <Text strong>Ship To</Text>
              <br />

              {data.shipping_contact_person && (
                <>
                  <Text>
                    Contact Person : +{data.shipping_contact_person}
                  </Text>
                  <br />
                </>
              )}

              <Text className="wrap-text">
                Address : {data.shipping_address}
              </Text>
              {data.shipping_po_box && (
                <>
                  <Text>
                    Po Box : {data.shipping_po_box}
                  </Text>
                  <br />
                </>
              )}

              {data.shipping_contact_no && (
                <>
                  <Text>
                    Contact No : +{data.shipping_contact_no}
                  </Text>
                  <br />
                </>
              )}
            </>
          )}
        </Col>



        {/* RIGHT: Invoice Meta */}
        <Col flex="0 0 280px" className="invoice-meta">
          <div className="invoice-meta-row">
            <span>Invoice No:</span>
            <span>{data.sale_voucher_no}</span>
          </div>

          <div className="invoice-meta-row">
            <span>Invoice Date:</span>
            <span>
              {formatToCompanyTimezone(data.created_date)?.split(" ")[0]}
            </span>
          </div>

          <div className="invoice-meta-row">
            <span>Address :</span>
            <span className="wrap-text">{institutionData?.street_address}</span>
          </div>

          {institutionData?.phone_no && <div className="invoice-meta-row">
            <span>Contact No:</span>
            <span>+{institutionData.phone_no || "-"}</span>
          </div>}
          {institutionData?.vat_license && <><div className="invoice-meta-row">
            <span>{institutionData?.country?.trim().toLowerCase() === "united arab emirates"
              ? "TRN No: "
              : "Tax Reg No: "}
            </span>
            <span className="wrap-text">{institutionData?.vat_license}</span>
          </div></>}

          <div className="invoice-meta-row">
            <span>Due Date:</span>
            <span>{data.due_date || "-"}</span>
          </div>

          {/* Balance Due UNDER INVOICE */}
          <div className="invoice-balance-due">
            <span>Total Balance Due</span>
            <strong>{numberFormat(accDue || 0)}</strong>
          </div>
        </Col>
      </Row>




      {/* ================= ITEMS ================= */}
      <Table
        className="invoice-items"
        dataSource={visibleItems}
        columns={columns}
        pagination={false}
        rowKey="item_id"
        bordered={false}
        rowClassName={(record) =>
          isBonusItem(record) ? "bonus-row" : ""
        }
      />
      {hasBonusItems && <Text style={{ display: "block", fontSize: 11, color: "#666", marginLeft: 10, marginTop: 10 }}>
        *Bonus items are supplied free of cost and not included in taxable value.
      </Text>}

      {/* ================= TOTALS (LAST PAGE ONLY) ================= */}
      <div className="invoice-totals">
        <div className="row">
          <span>Sub Total</span>
          <span>{numberFormat(isIndividual ? totals.subTotal : data.sub_total)}</span>
        </div>

        <div className="row">
          <span>Discount</span>
          <span>{numberFormat(isIndividual ? totals.discountTotal : data.total_discount)}</span>
        </div>

        {isIndiaGst ? (
          <>
            {data.place_of_supply_type == 'intra' &&
              <>       <div className="row">
                <span>CGST</span>
                {/* <span>{numberFormat(data.cgst_amount)}</span> */}
                <span>{numberFormatTax(data.cgst_amount)}</span>
              </div>

                <div className="row">
                  <span>SGST</span>
                  <span>{numberFormatTax(data.sgst_amount)}</span>
                </div></>
            }

            {data.place_of_supply_type == 'inter' &&
              <>
                <div className="row">
                  <span>IGST</span>
                  <span>{numberFormatTax(data.igst_amount)}</span>
                </div>
              </>}

            {data.gst_calculated == 'no' &&
              <>
                <div className="row">
                  <span>Total {taxLabel}</span>
                  <span>{numberFormat(totalTax)}</span>
                </div>
              </>}
          </>
        ) : (
          !isIndia && (
            <div className="row">
              <span>Total {taxLabel}</span>
              <span>{numberFormat(totalTax)}</span>
            </div>
          ))}
        {Number(data.other_service_cost) > 0 && (
          <div className="row">
            <span>Additional Expense</span>
            <span>{numberFormat(data.other_service_cost)}</span>
          </div>
        )}


        <div className="grand">
          <span>Grand Total ({currency})</span>
          <span>{numberFormat(data.total_amount)}</span>
        </div>

        <div className="row">
          <span>Paid</span>
          <span>{numberFormat(data.paid_total || 0)}</span>
        </div>

        <div className="row">
          <span>Invoice Due</span>
          <span>{numberFormat(data.due_amount || 0)}</span>
        </div>
      </div>

      {/* ================= PAYMENT BREAKDOWN (UI ONLY) ================= */}
      {!printMode && data?.trans?.length > 0 && (
        <>
          <Divider />
          <Row>
            <Col span={24}>
              <Text strong>Payment Breakdown</Text>
              <Table
                className="payment-breakdown-table"
                size="small"
                bordered
                pagination={false}
                style={{ marginTop: 8 }}
                rowKey={(_, index) => index}
                dataSource={data.trans.map((tran, index) => ({
                  key: index,
                  index: index + 1,
                  account: tran.to_acc_name || "N/A",
                  amount: parseFloat(
                    data?.conversion_rate && data.conversion_rate !== 0
                      ? tran.tran_amount * data.conversion_rate
                      : tran.tran_amount
                  ).toFixed(2),
                }))}
                columns={[
                  { title: "#", dataIndex: "index", width: 40 },
                  { title: "Account", dataIndex: "account" },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    align: "right",
                    width: 100,
                    render: (val) => numberFormat(val),
                  },
                ]}
              />

            </Col>
          </Row>
        </>
      )}

      <Divider />

      <Text className="in-words">
        In Words: {convertNumberToWords(grandTotal, currency)}
      </Text>

      {/* <div className="invoice-footer">
        <div>
          <Text strong>Notes</Text>
          <p>Thanks for your business.</p>
        </div>

        <div className="signature">
          <p>Signature</p>
          <div className="sign-line" />
        </div>
      </div> */}

      {(showStamp || showSignature) && (
        <div className="invoice-footer-section">

          <div className="footer-right-group">
            {showStamp && institutionData?.stamp && (
              <div className="stamp-block">
                <img src={institutionData.stamp} alt="Stamp" />
                <div className="sign-line-text"></div>
              </div>
            )}

            {showSignature && institutionData?.signature && (
              <div className="signature-block">
                <img src={institutionData.signature} alt="Signature" />
                <div className="sign-line-text">Authorized Signature</div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default SalesVoucherBody;
